import Minze, { MinzeElement, Reactive, EventListeners, Attrs } from 'minze'
import MainModule from '../../../modules/main'
import { Callback } from '../../../modules/callback'
import { MODALS } from '../../../constants'
import { Routing } from '../../../modules/routing'
import { EMAIL_REGEX, PASSWORD_REGEX, getCardPlaceholder } from '../../../modules/utils'
import { Locale } from '../../../modules/locale'
import { Font } from '../../../constants'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'

Locale.getInstance().registerKeySet('fr', 'create-event-first-recap', {
  title: "Résumé de l'événement",
  expositionTitle: 'Résumé de l’exposition',
  description:
    'Vous y êtes presque !  Si vous souhaitez modifier ou ajouter une représentation, cliquez sur “Modifier”. Si vous avez terminé, cliquez sur “Soumettre” pour envoyer le tout en validation par les équipes internes de La Vitrine.',
  expositionDescription:
    'Vous y êtes presque !  Si vous souhaitez modifier ou ajouter une représentation, cliquez sur “Modifier”. Si vous avez terminé, cliquez sur “Soumettre” pour envoyer le tout en validation par les équipes internes de La Vitrine.',
})

Locale.getInstance().registerKeySet('en', 'create-event-first-recap', {
  title: 'Event Recap',
  expositionTitle: 'Exposition Recap',
  description:
    "You're almost there!  If you wish to modify or add a representation, click on “Modify”. When you're finished, click on “Submit” to send the whole thing to La Vitrine's in-house teams for validation.",
  expositionDescription:
    "You're almost there!  If you wish to modify or add a representation, click on “Modify”. When you're finished, click on “Submit” to send the whole thing to La Vitrine's in-house teams for validation.",
})

export interface LvcCreateEventFirstRecap {
  flowGroup: string
  flowStep: string
  eventName: string
  eventImage: string
  eventDescription: string
  eventDisciplines: string
}

export interface LvcCreateEventFirstRecapData {
  eventName: string
  eventImage: string
  eventDescription: string
  eventDisciplines: string
}

export class LvcCreateEventFirstRecap
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private core: IFlowCore | undefined
  attrs: Attrs = [
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
    ['data-type', 'event'],
  ]

  reactive: Reactive = [
    ['eventImagePath', ''],
    ['eventImageUrl', ''],
    ['eventName', ''],
    ['eventDescription', ''],
    ['eventDisciplines', ''],
    ['eventFirstRecapData', {}],
  ]

  handleModalEvent = (event: any) => {
    this.eventName = event.detail
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)

    this.core = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // We get the data from the flowCore and we set the values of the inputs
    const entireEventData = data

    if (this.dataType === 'exhibition') {
      this.eventImagePath = entireEventData.createExhibitionAddImage.imageUrl
      this.eventImage = await MainModule.getInstance()
        .getAuth()
        .getImageUrl(entireEventData.createExhibitionAddImage.imageUrl)
      this.eventName =
        entireEventData.createExhibitionDescription[
          Locale.language === 'fr' ? 'eventNameFr' : 'eventNameEn'
        ]
      this.eventDescription =
        entireEventData.createExhibitionDescription[
          Locale.language === 'fr' ? 'eventDescriptionFr' : 'eventDescriptionEn'
        ]
      this.eventDisciplines = entireEventData.createExhibitionDescription.disciplines
    } else {
      if (entireEventData?.createEventSearchOrAdd?.eventData) {
        if (entireEventData?.createEventSearchOrAdd?.eventData?.createEventAddImage?.imageUrl) {
          this.eventImagePath = entireEventData?.createEventSearchOrAdd.eventData?.imageUrl
          this.eventImage = await MainModule.getInstance()
            .getAuth()
            .getImageUrl(entireEventData.createEventSearchOrAdd.eventData.imageUrl)
        }

        const thumbnailImageObject =
          entireEventData?.createEventSearchOrAdd.eventData?.medias?.[0]?.media
        let thumbnailImage = ''
        if (thumbnailImageObject) {
          const mediaObject = JSON.parse(decodeURIComponent(thumbnailImageObject))
          thumbnailImage = mediaObject?.[0]?.url
        }
        if (thumbnailImage) {
          this.eventImagePath = thumbnailImage
          this.eventImage = thumbnailImage
        } else {
          this.eventImagePath = getCardPlaceholder(
            entireEventData?.createEventSearchOrAdd.eventData?.type
          )
          this.eventImage = getCardPlaceholder(
            entireEventData?.createEventSearchOrAdd.eventData?.type
          )
        }
        this.eventName =
          entireEventData?.createEventSearchOrAdd.eventData?.[
            Locale.language === 'fr' ? 'nameFr' : 'nameEn'
          ]
        this.eventDescription =
          entireEventData?.createEventSearchOrAdd.eventData?.[
            Locale.language === 'fr' ? 'descriptionFr' : 'descriptionEn'
          ]
        // this.eventDisciplines = entireEventData?.createEventSearchOrAdd.eventData?.createEventDescription.disciplines;
        // this.representations = entireEventData?.createEventSearchOrAdd.eventData?.addRepresentation.representations;
      } else {
        this.eventImagePath = entireEventData.createEventAddImage.imageUrl
        this.eventImage = await MainModule.getInstance()
          .getAuth()
          .getImageUrl(entireEventData.createEventAddImage.imageUrl)
        this.eventName =
          entireEventData.createEventDescription[
            Locale.language === 'fr' ? 'eventNameFr' : 'eventNameEn'
          ]
        this.eventDescription =
          entireEventData.createEventDescription[
            Locale.language === 'fr' ? 'eventDescriptionFr' : 'eventDescriptionEn'
          ]
        this.eventDisciplines = entireEventData.createEventDescription.disciplines
      }
    }

    this.eventFirstRecapData = {
      image: this.eventImage,
      imagePath: this.eventImagePath,
      eventName: this.eventName,
      eventDescription: this.eventDescription,
      eventDisciplines: this.eventDisciplines,
      eventContributors: entireEventData.addEventContributors?.contributors || [],
    }

    this.rerender()
  }

  public async serialize(): Promise<unknown> {
    return {
      eventName: this.eventName,
      eventImage: this.eventImage,
      eventImagePath: this.eventImagePath,
      eventDescription: this.eventDescription,
      eventDisciplines: this.eventDisciplines,
    } as LvcCreateEventFirstRecapData
  }

  public async validate(): Promise<boolean> {
    return true
  }

  onContributorsButtonClick(): void {
    // go to contributors step
    this?.core?.prev()
  }

  html = () =>
    `<div class="container">


    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey(this.dataType === 'event' ? 'create-event-first-recap.title' : 'create-event-first-recap.expositionTitle')}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey(this.dataType === 'event' ? 'create-event-first-recap.description' : 'create-event-first-recap.expositionDescription')}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px">
      
      <lvc-creation-recap event-data="${encodeURIComponent(JSON.stringify(this.eventFirstRecapData))}" show-modify-button="false"></lvc-creation-recap>
    </lvc-box>

  </div>
    `

  eventListeners: EventListeners = [
    ['lvc-event-search-field', 'change', this.handleModalEvent.bind(this)],
    [
      'lvc-creation-recap',
      'lvc-event-recap:goToContributors',
      this.onContributorsButtonClick.bind(this),
    ],
  ]
}
